import React from 'react';
import { Braces, CheckCircle2, AlertTriangle, ShieldAlert, FileJson } from 'lucide-react';

const SchemaValidatorPanel = ({ project }) => {
  const schema = project?.technical_audit?.schema_validation || null;

  if (!schema || !schema.total_pages) {
    return (
      <div className="flex flex-col gap-6 animate-in fade-in duration-300">
        <div className="bg-black/30 border border-gray-850 p-8 rounded-2xl text-center">
          <FileJson size={32} className="text-gray-700 mx-auto mb-3" />
          <p className="text-sm text-gray-500">
            No structured data validation available. Re-run an audit to validate JSON-LD markup against Schema.org.
          </p>
        </div>
      </div>
    );
  }

  const coverage = schema.coverage_percentage || 0;
  const hasErrors = (schema.pages_with_errors || 0) > 0;

  return (
    <div className="flex flex-col gap-6 animate-in fade-in duration-300">

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-black/40 border border-gray-800/80 rounded-xl p-4 flex flex-col gap-1">
          <span className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">Schema Coverage</span>
          <span className={`text-xl font-mono font-black ${coverage >= 50 ? 'text-green-400' : coverage > 0 ? 'text-yellow-400' : 'text-red-400'}`}>
            {coverage}%
          </span>
          <span className="text-[10px] text-gray-600">{schema.pages_with_schema}/{schema.total_pages} pages with markup</span>
        </div>
        <div className="bg-black/40 border border-gray-800/80 rounded-xl p-4 flex flex-col gap-1">
          <span className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">Pages With Errors</span>
          <span className={`text-xl font-mono font-black ${hasErrors ? 'text-red-400' : 'text-green-400'}`}>
            {schema.pages_with_errors || 0}
          </span>
        </div>
        <div className="bg-black/40 border border-gray-800/80 rounded-xl p-4 flex flex-col gap-1">
          <span className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">Types Detected</span>
          <span className="text-xl font-mono font-black text-blue-400">{schema.types_detected?.length || 0}</span>
        </div>
        <div className="bg-black/40 border border-gray-800/80 rounded-xl p-4 flex flex-col gap-1">
          <span className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">Validation Status</span>
          <span className={`text-sm font-bold ${hasErrors ? 'text-red-400' : 'text-green-400'}`}>
            {hasErrors ? 'Issues Found' : 'All Valid'}
          </span>
        </div>
      </div>

      {/* Detected types */}
      {(schema.types_detected?.length > 0) && (
        <div className="bg-black/30 border border-gray-850 p-5 rounded-2xl flex flex-col gap-3">
          <h4 className="font-extrabold text-white text-sm flex items-center gap-2">
            <Braces size={15} className="text-purple-400" />
            Schema.org Types Detected
          </h4>
          <div className="flex flex-wrap gap-2">
            {schema.types_detected.map((t, i) => (
              <span key={i} className="px-2.5 py-1 rounded-lg bg-purple-950/50 text-purple-300 border border-purple-500/20 text-xs font-mono font-medium">
                {t}
              </span>
            ))}
          </div>
          <p className="text-[11px] text-gray-500">
            Validated against required/recommended property rules for Organization, Product, FAQ, Article, Breadcrumb,
            Service, LocalBusiness and more.
          </p>
        </div>
      )}

      {/* Invalid pages */}
      {(schema.invalid_pages?.length > 0) && (
        <div className="bg-red-950/15 border border-red-500/25 rounded-2xl p-5 flex flex-col gap-3">
          <h4 className="font-extrabold text-red-300 text-sm flex items-center gap-2">
            <ShieldAlert size={15} className="text-red-400" />
            Malformed or Invalid JSON-LD ({schema.invalid_pages.length} pages)
          </h4>
          <div className="flex flex-col gap-2">
            {schema.invalid_pages.map((page, i) => (
              <div key={i} className="bg-gray-950/80 border border-gray-850 rounded-xl p-3 flex flex-col gap-1.5">
                <a href={page.url} target="_blank" rel="noreferrer" className="font-mono text-xs text-blue-400 hover:underline truncate">
                  {page.url}
                </a>
                <div className="flex flex-col gap-1">
                  {page.errors?.map((err, eIdx) => (
                    <span key={eIdx} className="text-[10px] text-red-300/90 font-mono flex items-center gap-1.5">
                      <AlertTriangle size={10} className="shrink-0" /> {err}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Per-page matrix */}
      {schema.page_results?.length > 0 && (
        <div className="flex flex-col gap-3">
          <h4 className="font-extrabold text-white text-sm flex items-center gap-2">
            <FileJson size={15} className="text-blue-400" />
            Page-by-Page Structured Data Validation
          </h4>
          <div className="border border-gray-800/80 rounded-xl overflow-hidden bg-black/20">
            <table className="w-full text-left border-collapse text-[11px]">
              <thead>
                <tr className="bg-gray-950/70 border-b border-gray-800 text-gray-400">
                  <th className="p-3 font-bold">Page URL</th>
                  <th className="p-3 font-bold text-center">Scripts</th>
                  <th className="p-3 font-bold text-center">Valid</th>
                  <th className="p-3 font-bold text-center">Status</th>
                  <th className="p-3 font-bold">Types</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-850">
                {schema.page_results.map((r, idx) => (
                  <tr key={idx} className="hover:bg-gray-800/35 transition-colors">
                    <td className="p-3 font-mono text-gray-300 truncate max-w-[280px]" title={r.url}>
                      <a href={r.url} target="_blank" rel="noreferrer" className="hover:text-blue-300">{r.url}</a>
                    </td>
                    <td className="p-3 text-center font-mono text-gray-400">{r.total_scripts}</td>
                    <td className="p-3 text-center font-mono text-gray-400">{r.valid_scripts}</td>
                    <td className="p-3 text-center">
                      <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase ${
                        r.is_valid ? 'bg-green-950/40 text-green-400 border border-green-500/20' : 'bg-red-950/40 text-red-400 border border-red-500/20'
                      }`}>
                        {r.is_valid ? 'Valid' : 'Invalid'}
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="flex flex-wrap gap-1">
                        {r.types?.length ? r.types.map((t, ti) => (
                          <span key={ti} className="text-[9px] font-mono text-purple-300 bg-purple-950/40 border border-purple-500/20 px-1.5 py-0.5 rounded">
                            {t}
                          </span>
                        )) : <span className="text-gray-600 italic text-[10px]">None</span>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {hasErrors && (
        <div className="bg-gray-950/60 border border-gray-850 rounded-xl p-4 text-[11px] text-gray-400 leading-relaxed">
          <span className="text-gray-300 font-bold">How to fix: </span>
          Repair invalid JSON with the <span className="font-mono text-purple-300">Schema Markup Validator</span>, add required
          properties (e.g. <span className="font-mono">name</span> for Organization, <span className="font-mono">mainEntity</span> for
          FAQPage, <span className="font-mono">itemListElement</span> for BreadcrumbList), then test in Google's Rich Results Test.
        </div>
      )}

      {/* All-clear state */}
      {!hasErrors && (
        <div className="bg-green-950/20 border border-green-500/25 rounded-2xl p-5 flex items-center gap-3">
          <CheckCircle2 size={18} className="text-green-400 shrink-0" />
          <p className="text-xs text-green-200">
            All detected JSON-LD blocks parse cleanly and satisfy required Schema.org properties.
          </p>
        </div>
      )}
    </div>
  );
};

export default SchemaValidatorPanel;
